/**
 * ToPhoneNumber.js
 * Talkroute
 *
 * Created by Vel Genov on 4/08/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

import React from "react";
import { connect } from "react-redux";
import { Input, Label } from "semantic-ui-react";

import {
  updateNewConversationPhoneNumber,
  removeNewConversationPhoneNumber,
} from "@actions/conversationActions";

import { domIds } from "@constants/testConstants";
import uiString from "@constants/uiString";
import keyConstants from "@constants/keyConstants";

import { validatePhoneNumber } from "@services/dataClasses/CallData";

import { formatPhoneNumber } from "@helpers/functions/phoneNumberFormatter";
import CloseIcon from "@material-ui/icons/Close"
// Limit the phone number field to 11 digits - a standard US phone number + 1 for the country code
const MAX_NUMBER_DIGITS = 11;

interface IToPhoneNumberProps {
  phoneNumber: string;
  isDraft: boolean;
  updateNewConversationPhoneNumber: (number: string) => any;
  removeNewConversationPhoneNumber: () => any;
}

/**
 * Displays the "To:" field in a conversation
 *
 */
class ToPhoneNumber extends React.Component<IToPhoneNumberProps, any> {
  static defaultProps = {
    isDraft: false,
  };

  /**
   * Display an editable field
   */
  displayEntryField = () => {
    const { enteredPhoneNumber, isValidPhoneNumber } = this.state;

    return (
      <Input
        id={domIds.CONVERSATIONS_TO_PHONE_NUMBER_INPUT}
        size="mini"
        placeholder="Phone number"
        onChange={this.onHandleChange}
        onKeyDown={this.onInputFormKeyPress}
        value={enteredPhoneNumber}
        autoFocus
        error={!isValidPhoneNumber}
        onBlur={this.handleBlur}
      />
    );
  };

  /**
   * Handle any changes to the editable phone number field
   *
   * @param event a React synthetic event
   */
  onHandleChange = (event) => {
    const { value } = event.target;

    const unformattedNumber = value.replace(/\D/g, "");
    if (unformattedNumber.length <= MAX_NUMBER_DIGITS) {
      // Limit the number of digits to MAX_NUMBER_DIGITS
      const formattedValue = this.attemptToFormatNumber(value);

      this.setState({
        enteredPhoneNumber: formattedValue,
        isValidPhoneNumber: true,
      });
    }
  };

  /**
   * Try to format the phone number as typed in
   *
   * @param phoneNumber a string of the phone number to format
   * @returns a formatted phone number
   */
  attemptToFormatNumber = (phoneNumber) => {
    const formattedNumber = formatPhoneNumber(phoneNumber);
    return formattedNumber;
  };

  /**
   * Called each time a key is pressed on the input form. Checks for the "Enter" key
   * When "Enter" is pressed it validates the phone number entered
   *
   * @param event a React synthetic event
   */
  onInputFormKeyPress = (event) => {
    if (event.key === keyConstants.ENTER) {
      const { enteredPhoneNumber } = this.state;
      const validatedNumber = validatePhoneNumber(enteredPhoneNumber);

      event.preventDefault();
      if (validatedNumber === true) {
        this.phoneNumberValidated(enteredPhoneNumber);
      } else {
        this.setState({ isValidPhoneNumber: false });
      }
    }
  };

  /**
   * Called each time the phone number input field loses focus
   */
  handleBlur = () => {
    const { enteredPhoneNumber } = this.state;
    const validatedNumber = validatePhoneNumber(enteredPhoneNumber);

    if (validatedNumber === true) {
      this.phoneNumberValidated(enteredPhoneNumber);
    } else {
      this.setState({ isValidPhoneNumber: false });
    }
  };

  /**
   * Called when the user clicks the remove button
   */
  handleRemoveButton = () => {
    const {
      removeNewConversationPhoneNumber: removeNewConversationPhoneNumberLocal,
    } = this.props;

    removeNewConversationPhoneNumberLocal();
  };

  /**
   * Display the phone number
   * if it is the draft, display it with background color pale blue,
   * else display it without background
   *
   * @param phoneNumber the user entered formatted phone number
   */
  displayPhoneNumber = (phoneNumber) => {
    const { isDraft } = this.props;

    if (isDraft) {
      return (
        <span
          id={domIds.CONVERSATIONS_TO_PHONE_NUMBER_DRAFT_LABEL}
          style={{
            padding: "5px 15px 5px 5px",
            backgroundColor: "var(--color-bright-light-blue)",
            borderRadius: "5px",
            color: "var(--color-white)",
            position: "relative",
            cursor: "pointer"
          }}
          onClick={() => this.handleRemoveButton()}
          onKeyPress={() => {}}
          role="button"
          tabIndex={0}
        >
          {phoneNumber}
          <CloseIcon
            name="close"
            style={{
              position: "absolute",
              right: "2px",
              top: "3px",
              margin: "0px",
              padding: "0px",
              fill: "white",
              width: 12,
              height: 9
            }}
          />
        </span>
      );
    }

    return (
      <span id={domIds.CONVERSATIONS_TO_PHONE_NUMBER_LABEL}>{phoneNumber}</span>
    );
  };

  /**
   * Called when the entered phone number is validated
   * Fires a updateNewConversationPhoneNumber action
   *
   * @param phoneNumber the phone number entered
   */
  phoneNumberValidated = (phoneNumber) => {
    const {
      updateNewConversationPhoneNumber: updateNewConversationPhoneNumberLocal,
    } = this.props;

    this.setState({ isValidPhoneNumber: true });

    // Stripping all formatting
    const unformattedNumber = phoneNumber.replace(/\D/g, "");

    updateNewConversationPhoneNumberLocal(unformattedNumber);
  };

  /**
   * Displays a label indicating the phone number entered is invalid
   */
  showInvalidPhoneNumberLabel = () => {
    const { isValidPhoneNumber } = this.state;

    if (isValidPhoneNumber !== true) {
      return (
        <Label pointing="left" color="red">
          {uiString.INVALID_NUMBER}
        </Label>
      );
    }

    return null;
  };

  /**
   * The text currently in the phone number entry box
   */
  setEntryBoxPhoneNumber = (phoneNumber) =>
    this.setState({ enteredPhoneNumber: phoneNumber });

  constructor(props) {
    super(props);
    this.state = {
      enteredPhoneNumber: "",
      isValidPhoneNumber: true,
    };
  }

  /**
   * Check if the number to call changed when the props get updated
   *
   * @param {*} prevProps the previous props
   */
  componentDidUpdate(prevProps) {
    const { phoneNumber: prevphoneNumber } = prevProps;
    const { phoneNumber } = this.props;
    if (phoneNumber !== prevphoneNumber && phoneNumber === null) {
      this.setEntryBoxPhoneNumber("");
    }
  }

  render() {
    const { phoneNumber } = this.props;

    return (
      <div
        className="text-normal"
        style={{
          display: "inline-block",
          verticalAlign: "middle",
        }}
      >
        {`${uiString.conversationDetails.TO}: `}
        {phoneNumber !== null
          ? this.displayPhoneNumber(phoneNumber)
          : this.displayEntryField()}
        {/* this.showInvalidPhoneNumberLabel() */}
      </div>
    );
  }
}

/**
 * Map props to a redux state
 * @param {*} state
 */
const mapStateToProps = () => ({});

export default connect(mapStateToProps, {
  updateNewConversationPhoneNumber,
  removeNewConversationPhoneNumber,
})(ToPhoneNumber);
